export const GCT_TAX_CATEGORIES = Object.freeze({
  STANDARD_GCT_15: 'standard_gct_15',
  ZERO_RATED: 'zero_rated',
  EXEMPT: 'exempt',
  CUSTOM: 'custom',
});

export const STANDARD_GCT_RATE = 0.15;

const SUPPORTED_CATEGORIES = new Set(Object.values(GCT_TAX_CATEGORIES));

export function roundCurrency(value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return 0;
  return Math.round((numericValue + Number.EPSILON) * 100) / 100;
}

export function isSupermarketGctRegistered(supermarketData) {
  return supermarketData?.gct_registered === true || supermarketData?.gctRegistered === true;
}

export function normalizeGctRate(rate) {
  const numericRate = Number(rate);
  if (!Number.isFinite(numericRate) || numericRate <= 0) return 0;
  return numericRate > 1 ? numericRate / 100 : numericRate;
}

export function resolveInventoryGctConfig(inventoryData = {}, supermarketData = {}) {
  const rawCategory = inventoryData?.tax_category || inventoryData?.gct_category;
  const taxCategory = SUPPORTED_CATEGORIES.has(rawCategory)
    ? rawCategory
    : GCT_TAX_CATEGORIES.STANDARD_GCT_15;
  const supermarketGctRegistered = isSupermarketGctRegistered(supermarketData);

  if (!supermarketGctRegistered) {
    return {
      supermarketGctRegistered,
      taxCategory,
      taxRate: 0,
    };
  }

  if (taxCategory === GCT_TAX_CATEGORIES.STANDARD_GCT_15) {
    return {
      supermarketGctRegistered,
      taxCategory,
      taxRate: STANDARD_GCT_RATE,
    };
  }

  if (
    taxCategory === GCT_TAX_CATEGORIES.ZERO_RATED ||
    taxCategory === GCT_TAX_CATEGORIES.EXEMPT
  ) {
    return {
      supermarketGctRegistered,
      taxCategory,
      taxRate: 0,
    };
  }

  return {
    supermarketGctRegistered,
    taxCategory,
    taxRate: normalizeGctRate(inventoryData?.tax_rate ?? inventoryData?.gct_rate),
  };
}

export function calculateTaxInclusiveBreakdown({
  taxInclusiveAmount,
  taxCategory = GCT_TAX_CATEGORIES.STANDARD_GCT_15,
  taxRate = STANDARD_GCT_RATE,
  supermarketGctRegistered = true,
}) {
  const grossAmount = roundCurrency(taxInclusiveAmount);
  const effectiveRate = supermarketGctRegistered ? normalizeGctRate(taxRate) : 0;

  if (grossAmount <= 0 || effectiveRate <= 0) {
    return {
      taxCategory,
      taxRate: effectiveRate,
      taxInclusiveTotal: grossAmount,
      taxExclusiveTotal: grossAmount,
      gctIncludedTotal: 0,
    };
  }

  const gctIncludedTotal = roundCurrency(grossAmount - grossAmount / (1 + effectiveRate));
  const taxExclusiveTotal = roundCurrency(grossAmount - gctIncludedTotal);

  return {
    taxCategory,
    taxRate: effectiveRate,
    taxInclusiveTotal: grossAmount,
    taxExclusiveTotal,
    gctIncludedTotal,
  };
}

function getInventoryForCartItem(item, inventoryDetails) {
  if (!item || !inventoryDetails) return {};
  return (
    inventoryDetails[item.productId] ||
    inventoryDetails[item.inventoryId] ||
    inventoryDetails[item.id] ||
    {}
  );
}

function defaultLineTotal(item) {
  return roundCurrency((Number(item?.price) || 0) * (Number(item?.quantity) || 0));
}

export function calculateCartGctBreakdown({
  cartItems = [],
  inventoryDetails = {},
  supermarket = {},
  getLineTotal,
} = {}) {
  const lineItems = cartItems.map((item, index) => {
    const inventoryData = getInventoryForCartItem(item, inventoryDetails);
    const gctConfig = resolveInventoryGctConfig(inventoryData, supermarket);
    const lineTotal = typeof getLineTotal === 'function'
      ? getLineTotal(item, index)
      : item?.taxInclusiveLineTotal ?? item?.lineTotal ?? defaultLineTotal(item);
    const breakdown = calculateTaxInclusiveBreakdown({
      taxInclusiveAmount: lineTotal,
      ...gctConfig,
    });

    return {
      productId: item?.productId || null,
      name: item?.name || null,
      quantity: Number(item?.quantity) || 0,
      ...breakdown,
    };
  });

  const categoryMap = new Map();
  lineItems.forEach((lineItem) => {
    const key = `${lineItem.taxCategory}:${lineItem.taxRate}`;
    const current = categoryMap.get(key) || {
      taxCategory: lineItem.taxCategory,
      taxRate: lineItem.taxRate,
      taxInclusiveTotal: 0,
      taxExclusiveTotal: 0,
      gctIncludedTotal: 0,
      itemCount: 0,
    };

    current.taxInclusiveTotal = roundCurrency(
      current.taxInclusiveTotal + lineItem.taxInclusiveTotal
    );
    current.taxExclusiveTotal = roundCurrency(
      current.taxExclusiveTotal + lineItem.taxExclusiveTotal
    );
    current.gctIncludedTotal = roundCurrency(
      current.gctIncludedTotal + lineItem.gctIncludedTotal
    );
    current.itemCount += 1;
    categoryMap.set(key, current);
  });

  return {
    supermarketGctRegistered: isSupermarketGctRegistered(supermarket),
    taxInclusiveTotal: roundCurrency(
      lineItems.reduce((sum, item) => sum + item.taxInclusiveTotal, 0)
    ),
    taxExclusiveTotal: roundCurrency(
      lineItems.reduce((sum, item) => sum + item.taxExclusiveTotal, 0)
    ),
    gctIncludedTotal: roundCurrency(
      lineItems.reduce((sum, item) => sum + item.gctIncludedTotal, 0)
    ),
    breakdownByCategory: Array.from(categoryMap.values()),
    lineItems,
  };
}

export function applyGctBreakdownToCartItems(cartItems = [], gctSummary = {}) {
  const lineItems = Array.isArray(gctSummary.lineItems) ? gctSummary.lineItems : [];

  return cartItems.map((item, index) => {
    const lineItem = lineItems[index];
    if (!lineItem) return item;

    return {
      ...item,
      taxCategory: lineItem.taxCategory,
      taxRate: lineItem.taxRate,
      taxInclusiveLineTotal: lineItem.taxInclusiveTotal,
      taxExclusiveLineTotal: lineItem.taxExclusiveTotal,
      gctIncluded: lineItem.gctIncludedTotal,
    };
  });
}

export function parsePersistedGctSummary(value) {
  if (!value) return null;

  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch (_error) {
      return null;
    }
  }

  return typeof value === 'object' ? value : null;
}

export function resolvePersistedGctSummary(...sources) {
  for (const source of sources) {
    if (!source || typeof source !== 'object') continue;

    const summary = parsePersistedGctSummary(
      source.gctSummary ||
      source.gct_summary ||
      source.taxSummary ||
      source.tax_summary
    );

    if (summary) {
      return summary;
    }
  }

  return null;
}
